/* eslint-disable max-len */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import produce from 'immer';
import deepmerge from 'deepmerge';
import jwtDecode from 'jwt-decode';
import window from 'global/window';
import logger from '../common/logger';
import {
  LL_PRODUCER_API,
  LL_CONSUMER_API,
  LRS_VERB_CONSUMED,
  LRS_VERB_COMPLETED,
  PROD_ENV,
  LOCAL_STORAGE_LRS_KEY,
  LOCAL_STORAGE_ANONYMOUS_ID,
  RETRY_COUNT,
  OBJECT_TYPE_COURSE, OBJECT_TYPE_MODULE, OBJECT_TYPE_QUERY, SEARCH_API,
  OBJECT_TYPE_COLLECTION,
  LRS_APPID_AU,
  CLIENT_CONTEXT_CHANNEL,
  LIST_API_HOST,
  OBJECT_TYPE_PROBLEM,
  OBJECT_TYPE_UNIT,
  LRS_APPID_LRNO, LRS_APPID_CSO, LRS_APPID_AEX,
  LEARNING_MODE_COURSEWARE, LEARNING_MODE_MICROLEARNING,
  CLIENT_SOURCE_LRNO,
  LRS_VERB_ACKNOWLEDGED,
  ROUTER_BASENAME,
  PERSONALIZATION_EXPERIMENT_COMMERCIAL_ENTITLEMENT,
  PERSONALIZATION_EXPERIMENT_TRIAL_ENTITLEMENT,
} from '../common/constants';
import {
  generateURN,
  generateLRSContext,
  getEnvironmentConstant,
  oneWayHash,
  generateLRSID, getLLRecordFilter, addUniversalContext, validateURN, getCourseAPIHost,
} from '../common/utilities';
import { getDataState, decorateListData, getSearchResult } from './universalDataFetchers';
import XHRError from '../common/xhrError';
import authLib from '../common/pkce';
import { generateLRSConsumerCalls } from './lrsDataCalls';
import { getChildProperty } from '../common/object';

axiosRetry(axios, { retries: RETRY_COUNT });

const getMostRecentStatement = (record) => [...record.statements].sort((a, b) => {
  if (new Date(a.timestamp) > new Date(b.timestamp)) {
    return 1;
  }
  if (new Date(a.timestamp) < new Date(b.timestamp)) {
    return -1;
  }
  return 0;
})[0];

const getRecordType = (record) => {
  const mostRecentStatement = getMostRecentStatement(record);

  return mostRecentStatement.objectType;
};

const getDataFetchPromise = ({
  recordId, record, env, isPreview,
}) => {
  const recordType = getRecordType(record);
  const contentfulId = recordId.split('/').pop();
  switch (recordType) {
  case OBJECT_TYPE_COURSE:
    return getDataState({
      params: { course: contentfulId }, env, isPreview,
    }).catch((err) => ({ err: err.message }));
  case OBJECT_TYPE_MODULE:
    return getDataState({
      params: { module: contentfulId }, env, isPreview,
    }).catch((err) => ({ err: err.message }));
  case OBJECT_TYPE_COLLECTION:
    return getDataState({
      // Note: ID or Slug works for the API in the collectionSlug param
      params: { collectionSlug: contentfulId }, env, isPreview,
    }).catch((err) => ({ err: err.message }));
  default:
    return { err: 'Bad record type' };
  }
};

export const generateAlphaSorts = createAsyncThunk(
  'generateAlphaSorts',
  async ({ objectName, env, isPreview = false }, { rejectWithValue, getState }) => {
    if (!objectName || !env) {
      rejectWithValue(new XHRError({ statusCode: 500, message: 'Failed to pass objectName and/or env to generateAlphaSorts.' }));
    }

    const state = getState();
    const stateObj = { ...state.userData[objectName] };
    const updatedRecords = {};

    Object.keys(stateObj.records).forEach((key) => {
      updatedRecords[key] = {};
      if (!stateObj.records[key].data) {
        updatedRecords[key].data = getDataFetchPromise({
          recordId: key, record: stateObj.records[key], env, isPreview,
        });
        updatedRecords[key] = { ...updatedRecords[key], ...stateObj.records[key] };
      } else {
        updatedRecords[key] = { ...stateObj.records[key] };
      }
    });

    const dataPromises = Object.keys(updatedRecords)
      .map((recordId) => updatedRecords[recordId].data.then(
        (data) => ({ recordId, data }),
      ));
    return Promise.allSettled(
      dataPromises,
    ).then((data) => {
      const alphaASC = [...data.sort((a, b) => {
        if ((a?.value?.data?.title && !b?.value?.data?.title) || (a?.value?.data?.title < b?.value?.data?.title)) {
          return -1;
        }
        if ((!a?.value?.data?.title && b?.value?.data?.title) || (a?.value?.data?.title > b?.value?.data?.title)) {
          return 1;
        }
        return 0;
      })].map((obj) => obj?.value?.recordId);

      // Can't just reverse alpha because we want to keep the undefined at the end.
      const alphaDSC = [...data.sort((a, b) => {
        if ((a?.value?.data?.title && !b?.value?.data?.title) || (a?.value?.data?.title > b?.value?.data?.title)) {
          return -1;
        }

        if ((!a?.value?.data?.title && b?.value?.data?.title) || (a?.value?.data?.title < b?.value?.data?.title)) {
          return 1;
        }
        return 0;
      })].map((obj) => obj?.value?.recordId);

      const retObj = {};
      retObj[objectName] = {
        ...stateObj, records: updatedRecords, alphaASC, alphaDSC,
      };
      return retObj;
    });
  },
);

export const fetchUserData = createAsyncThunk(
  'userData',
  async () => {
    const auth = authLib.authClient;

    try {
      const { accessToken, idToken } = await new Promise((resolve, reject) => {
        auth.getAccessTokenSilently()
          .catch((e) => {
            reject(e);
            return null;
          })
          .then((at) => {
            if (at) {
              auth.getIDTokenSilently()
                .catch((e) => {
                  reject(e);
                  return null;
                })
                .then((it) => {
                  if (it) {
                    resolve({ accessToken: at, idToken: it });
                  }
                });
            }
          });
      });

      // Update login state for UH
      window.headerConfig.accessToken = accessToken;
      const evt = new CustomEvent('SSO_SIGNED_IN', { detail: { at: accessToken } });
      window.dispatchEvent(evt);

      const decodedIdToken = jwtDecode(idToken);
      const mappedData = {
        userid: decodedIdToken.userid,
        oa2Token: accessToken,
        first: decodedIdToken.first_name,
        last: decodedIdToken.last_name,
        email: decodedIdToken.user_email,
      };

      return Promise.resolve(mappedData);
    } catch (e) {
      if (!e.toString().includes('Login required')) {
        logger.error('PKCE ERROR w/ Login Flow:', e);
      }
      return Promise.reject(new Error('No PKCE Login'));
    }
  },
);

export const fetchUserProfileData = createAsyncThunk(
  'fetchUserProfileData',
  async ({
    oa2Token, userId, env, apiVersion = 'v1',
  }) => {
    // eslint-disable-next-line consistent-return
    if (!oa2Token || !env || !userId) {
      return Promise.reject();
    }
    const host = getCourseAPIHost({ env, isPreview: false });

    // eslint-disable-next-line max-len
    const serviceURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/userprofile/${apiVersion}`;
    logger.info(`Calling getUserProfileData: ${serviceURL}`);

    return axios.post(serviceURL, { oa2Token, userId })
      .then((profileData) => {
        logger.info(`getting user profile data: ${JSON.stringify(profileData)}`);
        return profileData.data;
      }).catch((e) => Promise.reject(e));
  },
);

export const fetchLearningRecords = createAsyncThunk(
  'FetchLearningRecords',
  async ({
    uid, recordList = [], env, typeList = [], appVersion, generateDashboardGrouping = false, generateIndexGrouping = false, isPreview, oa2Token,
  }, { rejectWithValue }) => {
    if (!uid || !oa2Token) {
      // Attempt to return anonymous records
      const localRecords = JSON.parse(localStorage.getItem(LOCAL_STORAGE_LRS_KEY));
      if (localRecords) {
        const anonymousRecords = localRecords[LOCAL_STORAGE_ANONYMOUS_ID];
        if (anonymousRecords) {
          return Promise.resolve({ data: anonymousRecords });
        }
      }
      return Promise.resolve({});
    }
    const postURL = getEnvironmentConstant({ env, key: LL_CONSUMER_API });

    const axiosCalls = generateLRSConsumerCalls({
      recordList, appVersion, uid, postURL, typeList, oa2Token,
    });

    return Promise.all(axiosCalls)
      .then((resultObjects) => {
        const data = Object.assign(...resultObjects);
        // Filter Data to remove records from other environments
        Object.keys(data).forEach((key) => {
          if (!key.startsWith(getLLRecordFilter({ env }))) {
            delete data[key];
          }
        });
        // Filter Data to remove records from AU
        Object.keys(data).forEach((key) => {
          if (data[key]?.statements?.length < 1 || data[key].statements.filter((statement) => statement.application !== LRS_APPID_AU).length < 1) {
            delete data[key];
          }
        });

        return {
          data, env, generateDashboardGrouping, generateIndexGrouping, isPreview,
        };
      }).catch((err) => {
        const xhrErr = new XHRError(err?.response?.status || 500, err.message);
        return rejectWithValue(xhrErr);
      });
  },
);

export const fetchAllPersonalizedContent = createAsyncThunk(
  'fetchAllPersonalizedContent',
  async ({
    env = 'stg', apiVersion = 'v1', product, productValue, returnCount = 20,
  }, { rejectWithValue }) => {
    const host = getCourseAPIHost({ env, isPreview: false });
    const body = {
      product,
      role: [],
      returnCount,
    };
    const serviceURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/personalizedContent/${apiVersion}`;
    const contentTypes = ['course', 'module', 'quickstart', 'collection', 'tutorial', 'curatedlist'];

    try {
      const requests = contentTypes.map((contentType) => axios.post(serviceURL, { ...body, contentType: [contentType] }));

      const results = await Promise.allSettled(requests);

      const [courses, modules, quickStarts, collections, tutorials, curated] = results.map((result) => (result.status === 'fulfilled' ? result.value.data : []));

      const filterValue = productValue.toString();
      let searchResult = [];
      if (filterValue) {
        searchResult = await getSearchResult({
          catalogResult: { page: 0, perPage: 1 }, presetFilterString: `/product/${productValue}`, env, apiVersion,
        });
      }

      const allPersonalizedContent = {
        courses,
        modules,
        quickStarts,
        collections,
        tutorials,
        curated,
        filters: searchResult.length ? searchResult[0]?.filters : [],
        filterGroups: searchResult.length ? searchResult[0]?.filterGroups : [],
      };

      return allPersonalizedContent;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchListsData = createAsyncThunk(
  'FetchListsData',
  async ({
    oa2Token, env, isPreview = false, appVersion,
  }) => {
    if (!oa2Token) {
      return Promise.reject();
    }

    const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists?expandLists=true&targetChannels=${getEnvironmentConstant({ key: CLIENT_CONTEXT_CHANNEL, env })}`;
    logger.info(`Calling FetchListsData: ${serviceURL}`);

    return axios.get(serviceURL, {
      headers: {
        Authorization: `Bearer ${oa2Token}`,
        'X-GnosisVersion': appVersion,
      },
    }).then((listData) => {
      if (listData?.data?.items) {
        const filteredList = listData?.data?.items?.map((list) => ({ ...list, listItems: list?.listItems.filter((v, i, a) => a.findIndex((t) => (t.resourceId === v.resourceId)) === i) }));
        return decorateListData({
          env, isPreview, listArray: filteredList, appVersion,
        });
      }
      return [];
    });
  },
);

export const addListItem = createAsyncThunk(
  'addListItem',
  async ({
    oa2Token, env, listObject, listItemId, type, appVersion,
  }) => {
    if (!oa2Token || !env) {
      return Promise.reject();
    }

    // eslint-disable-next-line max-len
    const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists/${listObject.listId}/item?itemId=${listItemId}&type=${type}&targetChannels=${listObject.targetChannels || CLIENT_SOURCE_LRNO}`;
    logger.info(`Calling addListItem: ${serviceURL}`);

    return axios.patch(serviceURL, {}, {
      headers: {
        Authorization: `Bearer ${oa2Token}`,
        'X-GnosisVersion': appVersion,
      },
    }).then((listData) => decorateListData({
      env, isPreview: false, listArray: [listData?.data], appVersion,
    }).then((arrayData) => arrayData[0]));
  },
);

export const deleteListItem = createAsyncThunk(
  'deleteListItem',
  async ({
    oa2Token, env, listObject, listItemId, appVersion,
  }) => {
    // eslint-disable-next-line consistent-return
    if (!oa2Token || !env) {
      return Promise.reject();
    }

    // eslint-disable-next-line max-len
    const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists/${listObject.listId}/item?itemId=${listItemId}&targetChannels=${listObject.targetChannels || CLIENT_SOURCE_LRNO}`;
    logger.info(`Calling deleteListItem: ${serviceURL}`);

    return axios.delete(serviceURL, {
      headers: {
        Authorization: `Bearer ${oa2Token}`,
        'X-GnosisVersion': appVersion,
      },
    }).then((listData) => {
      logger.info(`deleteListItem data: ${JSON.stringify(listData)}`);
      const decoratedData = decorateListData({
        env, isPreview: false, listArray: [listData?.data], appVersion,
      }).then((decoratedArray) => decoratedArray[0]);
      logger.info(`deleteListItem decorated data: ${JSON.stringify(decoratedData)}`);
      return decoratedData;
    });
  },
);

export const deleteListData = createAsyncThunk(
  'deleteListData',
  async ({
    oa2Token, env, listObject, appVersion,
  }) => {
    if (!oa2Token || !env) {
      return Promise.reject();
    }
    const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists/${listObject.listId}?targetChannels=${listObject.targetChannels || CLIENT_SOURCE_LRNO}`;
    logger.info(`Calling deleteListData: ${serviceURL}`);

    return axios.delete(serviceURL, {
      headers: {
        Authorization: `Bearer ${oa2Token}`,
        'X-GnosisVersion': appVersion,
      },
    }).then(() => Promise.resolve(listObject.listId), (err) => Promise.reject(err));
  },
);

export const updateListData = createAsyncThunk(
  'UpdateListData',
  async ({
    oa2Token, env, listObject, appVersion,
  }, { rejectWithValue }) => {
    // console.warn('update', { oa2Token, env, listObject });
    if (!oa2Token || !env) {
      return Promise.reject();
    }

    const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists/${listObject.listId}?targetChannels=${listObject.targetChannels || CLIENT_SOURCE_LRNO}`;
    logger.info(`Calling updateListData: ${serviceURL}`);

    const strippedListObject = {
      title: listObject?.title,
      listItems: listObject?.listItems?.map((item) => {
        const strippedItem = { ...item };
        delete strippedItem.data;
        return strippedItem;
      }),
      accessType: listObject?.accessType,
    };
    if (listObject?.description) {
      strippedListObject.description = listObject.description;
    }

    // Add Context to listObject
    if (listObject?.targetChannels) {
      strippedListObject.targetChannels = listObject.targetChannels;
    } else {
      strippedListObject.targetChannels = getEnvironmentConstant({ key: CLIENT_CONTEXT_CHANNEL, env });
    }

    return axios.put(serviceURL, strippedListObject, {
      headers: {
        Authorization: `Bearer ${oa2Token}`,
        'X-GnosisVersion': appVersion,
      },
    }).then(
      (listData) => decorateListData({
        env, isPreview: false, listArray: [listData?.data], appVersion,
      }).then((arrayData) => arrayData[0]),
      (e) => {
        const xhrErr = new XHRError(e?.response?.status || 500, e?.response?.data?.detail || e.message);
        return rejectWithValue(xhrErr);
      },
    );
  },
);

export const createListData = createAsyncThunk(
  'CreateListData',
  async ({
    oa2Token, env, listObject, appVersion,
  }, { rejectWithValue }) => {
    if (!oa2Token || !env) {
      return Promise.reject();
    }
    const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists?targetChannels=${CLIENT_SOURCE_LRNO}`;
    logger.info(`Calling CreateListData: ${serviceURL}`);

    // Add Context to listObject
    if (!listObject?.targetChannels) {
      // eslint-disable-next-line no-param-reassign
      listObject.targetChannels = CLIENT_SOURCE_LRNO;
    }

    return axios.post(serviceURL, listObject, {
      headers: {
        Authorization: `Bearer ${oa2Token}`,
        'X-GnosisVersion': appVersion,
      },
    }).then(
      (listData) => decorateListData({
        env, isPreview: false, listArray: [listData?.data], appVersion,
      }).then((arrayData) => arrayData[0]),
      (e) => {
        const xhrErr = new XHRError(e?.response?.status || 500, e?.response?.data?.detail || e.message);
        return rejectWithValue(xhrErr);
      },
    );
  },
);

export const fetchAcknowledgedCourses = createAsyncThunk(
  'FetchAcknowledgedCourses',
  async ({
    uid, env, appVersion, oa2Token,
  }) => {
    const postURL = getEnvironmentConstant({ env, key: LL_CONSUMER_API });
    return axios
      .post(postURL, {
        userid: uid,
        objectTypes: [OBJECT_TYPE_COURSE],
        verbTypes: [LRS_VERB_ACKNOWLEDGED],
        latestOnly: true,
      }, { headers: { 'X-GnosisVersion': appVersion, Authorization: `Bearer ${oa2Token}` } })
      .catch((err) => {
        logger.error('Failed getting acknowledged courses:', err);
        return { data: null };
      })
      .then((res) => res.data);
  },
);

export const sendUserData = createAsyncThunk(
  'SendUserData',
  async ({
    uid, verb, curatedData, tutorialId, problemId, unitId, env, type, stateString, appVersion, purpose, tutorialData, collectionId, oa2Token,
  }) => {
    if (uid && uid?.length < 4) {
      logger.error(`INVALID UID: |${uid}|`);
    }
    if (!uid || uid?.length < 4 || !oa2Token) {
      return Promise.resolve();
    }

    if (!curatedData && tutorialData && !tutorialId) {
      // eslint-disable-next-line no-param-reassign
      tutorialId = tutorialData.id;
    }

    const postURL = getEnvironmentConstant({ env, key: LL_PRODUCER_API });
    let lrsId = '';
    switch (type) {
    case OBJECT_TYPE_PROBLEM:
      lrsId = generateLRSID({ id: problemId, env });
      break;
    case OBJECT_TYPE_UNIT:
      lrsId = generateLRSID({ id: unitId, env });
      break;
    case OBJECT_TYPE_COURSE:
      lrsId = generateLRSID({ id: curatedData.id, env });
      break;
    case OBJECT_TYPE_MODULE:
      lrsId = generateLRSID({ id: tutorialId, env });
      break;
    case OBJECT_TYPE_QUERY:
      lrsId = `${getEnvironmentConstant({ env, key: SEARCH_API })}/${stateString}`;
      break;
    case OBJECT_TYPE_COLLECTION:
      lrsId = generateLRSID({ id: collectionId, env });
      break;
    default:
      return null;
    }

    const URN = generateURN({
      uid, verb, lrsId, type,
    });
    const context = generateLRSContext({
      curatedData, tutorialId, env, stateString, purpose, tutorialData, unitId,
    });

    if (!validateURN(URN)) {
      logger.error(`Failed in SendUserData - invalid URN: ${URN} posting to ${postURL}`);
      return null;
    }

    if (!appVersion) {
      logger.error('App Version not defined - URN: ', URN);
    }

    return axios
      .post(postURL, { event: URN, context: addUniversalContext({ context, env }) }, { headers: { 'X-GnosisVersion': appVersion, Authorization: `Bearer ${oa2Token}` } })
      .catch((err) => {
        logger.error(`Failed in SendUserData - URN: ${URN} posting to ${postURL}`, err);
      }) // TODO: Handle error state
      .then((res) => res.json);
  },
);

const updateLearningRecords = ({
  learningRecords, payload, id, env,
}) => {
  let data;
  let allSavedRecords = '{}';
  const idKey = id ? `${oneWayHash(id)}-${id}` : LOCAL_STORAGE_ANONYMOUS_ID;
  try {
    allSavedRecords = localStorage.getItem(LOCAL_STORAGE_LRS_KEY) || '{}';
    const savedRecords = JSON.parse(allSavedRecords)[idKey] || {};
    Object.keys(savedRecords).forEach((key) => {
      if (!key.startsWith(getLLRecordFilter({ env }))) {
        delete savedRecords[key];
      } else {
        // Clean Malformed data
        if (savedRecords[key].statements) {
          savedRecords[key].statements = savedRecords[key].statements.filter((stat) => !!stat.context);
        }
        if (savedRecords[key].context) {
          delete savedRecords[key].context;
        }
        if (savedRecords[key].application) {
          delete savedRecords[key].application;
        }
        if (savedRecords[key]?.statements?.length < 1) {
          delete savedRecords[key];
        }
      }
    });
    data = deepmerge(savedRecords, payload);
  } catch (e) {
    logger.error('Failed parsing local storage ', e);
    data = payload;
  }

  const returnedRecords = produce(learningRecords, (draftState) => {
    Object.keys(data).forEach((itemId) => {
      // Dedupe statements
      const filteredStatementsArr = data[itemId].statements
        .filter((statement) => statement.application !== LRS_APPID_AU)
        .map((statement) => [JSON.stringify(statement), statement]);
      const filteredStatementsMap = new Map(filteredStatementsArr);
      const filteredStatements = [...filteredStatementsMap.values()];
      const record = { ...data[itemId], statements: filteredStatements };
      if (filteredStatements) {
        if (learningRecords[itemId]) {
        // Merge statements with existing record
          record.statements
            .filter((statement) => statement.application !== LRS_APPID_AU) // Filter out statements for other apps
            .forEach((statement) => {
              const { verb, context } = statement;
              const matchingStatement = draftState[itemId].statements.filter((s) => s.verb === verb && s?.context?.parent === context?.parent);
              if (matchingStatement.length) {
                // Statement for this verb / context exists so compare timestamps
                if (new Date(matchingStatement[0].timestamp) < new Date(statement.timestamp)) {
                  // New timestamp is greater so update
                  matchingStatement[0] = { ...matchingStatement[0], timestamp: statement.timestamp };
                }
              } else {
                // Statement for this verb / context doesn't exist yet so push it
                if (!draftState[itemId]) {
                  draftState[itemId] = {};
                }
                if (!draftState[itemId].statements) {
                  draftState[itemId].statements = [];
                }
                draftState[itemId] = { ...draftState[itemId], statements: [...draftState[itemId].statements, statement] };
              }
            });
        } else {
          draftState[itemId] = record;
        }
      }
    });
  });

  try {
    const curData = {};
    curData[idKey] = returnedRecords;
    const parsedSaved = JSON.parse(allSavedRecords);
    const mergedData = deepmerge(parsedSaved, curData);
    // Dedupe statements
    Object.keys(mergedData[idKey]).forEach((storeKey) => {
      mergedData[idKey][storeKey].statements = mergedData[idKey][storeKey].statements.filter(
        (v, i, a) => a.findIndex(
          (t) => t.verb === v.verb,
        ) === i,
      );
    });
    localStorage.setItem(LOCAL_STORAGE_LRS_KEY, JSON.stringify(mergedData));
  } catch (e) {
    logger.error('Failed setting local storage', e);
  }

  return returnedRecords;
};

export const userSlice = createSlice({
  name: 'userData',
  initialState: {
    experimentationContext: {},
    userDetails: {},
    personalizedData: {},
    personalizedQuickStart: [],
    learningRecords: {},
    listsData: [],
    curatedData: {},
    dashboardState: {},
    selectedSort: 'alpha',
    isLoaderVisible: false,
    fetchingLearningRecords: false,
    fetchingListsData: false,
    hasFetchedListsData: false,
    hasFetchedLearningRecords: false,
    authChecked: false,
    userViewCountExceeded: false,
    lastRecordSetDate: new Date().toUTCString(),
    preferredVolume: 0.5,
    isEditingList: false,
    seeAllView: false,
    selectedBookmarkSort: 'recent',
    selectedListsSort: 'recent',
    isUserProfileFetched: false,
    isPersonalizedContentLoading: false,
    showLoginPrompt: true,
  },
  reducers: {
    // toggleDashboardDrawerState: (state, action) => {
    //   const newState = produce(state, (draftState) => {
    //     draftState.dashboardState[action?.payload] = draftState.dashboardState[action?.payload] !== true;
    //   });
    //   return newState;
    // },
    setSelectedSort: (state, action) => ({
      ...state,
      selectedSort: action?.payload || 'alpha',
    }),
    setUserDetails: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.userDetails = action?.payload || {};
      });
      return newState;
    },
    // setPreferredVolume: (state, action) => {
    //   const newState = produce(state, (draftState) => {
    //     draftState.preferredVolume = action?.payload || 0.5;
    //   });
    //   return newState;
    // },
    setLearningRecords: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.learningRecords = updateLearningRecords({
          learningRecords: state?.learningRecords,
          payload: action?.payload?.learningRecords || [],
          id: draftState.userDetails.userid,
          env: action?.payload?.env || PROD_ENV,
        });
        draftState.lastRecordSetDate = new Date().toUTCString();
      });
      return newState;
    },
    clearLearningRecords: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.learningRecords = {};
        draftState.cleared = true;
        draftState.hasFetchedLearningRecords = false;
      });

      return newState;
    },
    setExperimentationContext: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.experimentationContext = action?.payload;
      });

      return newState;
    },
    setIsEditingList: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.isEditingList = action?.payload;
      });
      return newState;
    },
    setRecentRecordDataPromises: (state, action) => {
      // Required Props:
      // objectName: 'tutorialsAndCuratedLists', 'coursesAndModules', or 'collections'
      // start: array index integer for start of records to set
      // end: array index integer for end of records to set
      // env: env value eg. PROD_ENV
      // isPreview: boolean for preview state

      if (action?.payload?.objectName
        && state[action?.payload?.objectName]
        && Number.isInteger(action?.payload?.start)
        && Object.hasOwnProperty.call(action?.payload, 'start')
        && action?.payload?.start > -1
        && action?.payload?.end
        && Number.isInteger(action?.payload?.end)
        && action?.payload?.end > action?.payload?.start
        && action?.payload?.env) {
        const newReocordGrouping = produce(state, (draftState) => {
          const recordGrouping = draftState[action?.payload?.objectName];
          for (let idx = action?.payload?.start; idx <= action?.payload?.end; idx += 1) {
            if (recordGrouping?.recent
            && recordGrouping.recent[idx]
            && recordGrouping?.records
            && recordGrouping.records[recordGrouping.recent[idx]]
            && !recordGrouping.records[recordGrouping.recent[idx]].data) {
              recordGrouping.records[recordGrouping.recent[idx]].data = getDataFetchPromise({
                recordId: recordGrouping.recent[idx], record: recordGrouping.records[recordGrouping.recent[idx]], env: action?.payload?.env, isPreview: action?.payload?.isPreview || false,
              });
            }
          }
          draftState[action?.payload?.objectName] = recordGrouping;
        });

        return newReocordGrouping;
      }

      logger.error(`Invalid action sent to setRecentRecordDataPromises: ${JSON.parse(action?.payload) || '[invalid object]'}`);
      return state;
    },
    setCompletionRecordDataPromises: (state, action) => {
      // Required Props:
      // objectName: 'tutorialsAndCuratedLists', 'coursesAndModules', or 'collections'
      // start: array index integer for start of records to set
      // end: array index integer for end of records to set
      // env: env value eg. PROD_ENV
      // isPreview: boolean for preview state

      if (action?.payload?.objectName
        && state[action?.payload?.objectName]
        && Object.hasOwnProperty.call(action?.payload, 'start')
        && Number.isInteger(action?.payload?.start)
        && action?.payload?.start > -1
        && action?.payload?.end
        && Number.isInteger(action?.payload?.end)
        && action?.payload?.end > action?.payload?.start
        && action?.payload?.env
      ) {
        const newState = produce(state, (draftState) => {
          const recordGrouping = { ...draftState[action?.payload?.objectName] };

          if (recordGrouping && !recordGrouping.completion) {
          // Completion order hasn't been calculated yet.  Do it.

            const completion = Object.keys(recordGrouping.records);
            completion.sort((a, b) => {
              const aRecord = recordGrouping.records[a];
              const bRecord = recordGrouping.records[b];

              // aRecord has complete / consumed and bRecord does not
              if (aRecord.statements.some((statement) => statement.verb === LRS_VERB_COMPLETED || statement.verb === LRS_VERB_CONSUMED)
              && !bRecord.statements.some((statement) => statement.verb === LRS_VERB_COMPLETED || statement.verb === LRS_VERB_CONSUMED)) {
                return 1;
              }

              // bRecord has complete / consumed and aRecord does not
              if (bRecord.statements.some((statement) => statement.verb === LRS_VERB_COMPLETED || statement.verb === LRS_VERB_CONSUMED)
              && !aRecord.statements.some((statement) => statement.verb === LRS_VERB_COMPLETED || statement.verb === LRS_VERB_CONSUMED)) {
                return -1;
              }

              // Both records are of the same completion status so do secondary recent sort
              if (new Date(getMostRecentStatement(aRecord).timestamp) > new Date(getMostRecentStatement(bRecord).timestamp)) {
                return 1;
              }

              if (new Date(getMostRecentStatement(aRecord).timestamp) < new Date(getMostRecentStatement(bRecord).timestamp)) {
                return -1;
              }

              return 0;
            });

            recordGrouping.completion = completion;
          }

          // Set data promises on requested indices in the completion array.
          for (let idx = action?.payload?.start; idx <= action?.payload?.end; idx += 1) {
            if (recordGrouping?.completion
            && recordGrouping.completion[idx]
            && recordGrouping?.records
            && recordGrouping.records[recordGrouping.completion[idx]]
            && !recordGrouping.records[recordGrouping.completion[idx]].data) {
              recordGrouping.records[recordGrouping.completion[idx]].data = getDataFetchPromise({
                recordId: recordGrouping.completion[idx], record: recordGrouping.records[recordGrouping.completion[idx]], env: action?.payload?.env, isPreview: action?.payload?.isPreview || false,
              });
            }
          }

          draftState[action?.payload?.objectName] = recordGrouping;
        });

        return newState;
      }

      logger.error(`Invalid action sent to setCompletionRecordDataPromises: ${JSON.parse(action?.payload) || '[invalid object]'}`);
      return state;
    },
    enabledSeeAllView: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.seeAllView = action?.payload;
      });
      return newState;
    },
    setSelectedBookmarkSort: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.selectedBookmarkSort = action?.payload || 'recent';
      });
      return newState;
    },
    setSelectedListsSort: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.selectedListsSort = action?.payload || 'recent';
      });
      return newState;
    },
    dismissLogin: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.showLoginPrompt = !action?.payload;
      });
      return newState;
    },
  },
  extraReducers: {
    [generateAlphaSorts.pending]: (state) => ({
      ...state,
    }),
    [generateAlphaSorts.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        Object.keys(action.payload).forEach((key) => {
          draftState[key] = action.payload[key];
        });
      });
      return newState;
    },
    [generateAlphaSorts.rejected]: (state) => ({
      ...state,
    }),
    [sendUserData.pending]: (state) => ({
      ...state,
      sendingToProducer: true,
    }),
    [sendUserData.fulfilled]: (state) => {
      // User data has been returned.
      const newState = {
        sendingToProducer: false,
      };

      return {
        ...state,
        ...newState,
      };
    },
    [sendUserData.rejected]: (state) => {
      // User data has been returned.
      const newState = {
        sendingToProducer: false,
      };

      return {
        ...state,
        ...newState,
      };
    },
    [fetchUserData.pending]: (state) => ({
      ...state,
      // Don't reset values now that we have multiple checks
      // isLoaderVisible: true,
      // authChecked: false,
    }),
    [fetchUserData.fulfilled]: (state, action) => {
      logger.warn('fetchUserData.fulfilled');
      // User data has been returned.
      const newState = {
        isLoaderVisible: false,
        authChecked: true,
      };

      newState.userDetails = action?.payload || {};
      return {
        ...state,
        ...newState,
      };
    },
    [fetchUserData.rejected]: (state, err) => {
      // User is not logged in. Ensure removal of user-data from store.
      logger.warn('fetchUserData.rejected - User was not logged in', err);
      return {
        ...state,
        userDetails: {},
        isLoaderVisible: false,
        authChecked: true,

      };
    },
    [fetchUserProfileData.pending]: (state) => ({
      ...state,
    }),
    [fetchUserProfileData.fulfilled]: (state, action) => {
      logger.warn('fetchUserProfileData.fulfilled');
      const {
        roleName, roleId, industrySubsegmentIds, industries, entitlements,
      } = action?.payload;
      const userInfo = {
        ...state.userDetails, roleName, roleId, industrySubsegmentIds, industries, entitlements,
      };
      return {
        ...state,
        ...{
          userDetails: userInfo,
          isUserProfileFetched: true,
        },
      };
    },
    [fetchUserProfileData.rejected]: (state, err) => {
      logger.warn('fetchUserData.rejected', err);
      return {
        ...state,
        isUserProfileFetched: true,
      };
    },
    [fetchAllPersonalizedContent.pending]: (state) => ({
      ...state,
      isPersonalizedContentLoading: true,
    }),
    [fetchAllPersonalizedContent.fulfilled]: (state, action) => ({
      ...state,
      ...{
        personalizedData: action?.payload,
        isPersonalizedContentLoading: false,
      },
    }),
    [fetchAllPersonalizedContent.rejected]: (state) => ({
      ...state,
      isPersonalizedContentLoading: false,
    }),
    [fetchLearningRecords.pending]: (state) => ({
      ...state,
      hasFetchedLearningRecords: false,
      fetchingLearningRecords: true,
    }),
    [fetchLearningRecords.fulfilled]: (state, action) => {
      // User data has been returned.
      logger.debug('fetchLearningRecords.fulfilled');

      const env = action?.payload?.env || PROD_ENV;
      const isPreview = action?.payload?.isPreview || false;

      const updatedLearningRecords = updateLearningRecords({
        learningRecords: state?.learningRecords,
        payload: action?.payload?.data || [],
        id: state?.userDetails.userid,
        env,
      });

      const allStatementsHaveParent = (record) => record.statements.filter((statement) => (statement?.context?.parent !== undefined && statement?.context?.parent !== '')).length === record.statements.length;

      const orderRecentRecordIds = (records) => [...Object.keys(records).sort((a, b) => {
        if (new Date(records[a].lastInteraction) > new Date(records[b].lastInteraction)) {
          return -1;
        }

        if (new Date(records[a].lastInteraction) < new Date(records[b].lastInteraction)) {
          return 1;
        }

        return 0;
      })];

      const newState = produce(state, (draftState) => {
        draftState.cleared = false;
        draftState.fetchingLearningRecords = false;
        draftState.hasFetchedLearningRecords = true;
        draftState.lastRecordSetDate = new Date().toUTCString();
        draftState.learningRecords = updatedLearningRecords;

        if (action?.payload?.generateDashboardGrouping || action?.payload?.generateIndexGrouping) {
          const startTime = new Date();
          // Generate dashboard grouping
          const courseRecords = {};
          const curatedListRecords = {};
          const moduleRecords = {};
          const tutorialRecords = {};
          const collectionRecords = {};
          const unitRecords = {};
          Object.keys(updatedLearningRecords).forEach((recordId) => {
            const record = updatedLearningRecords[recordId];
            const mostRecentStatement = getMostRecentStatement(record);
            const recordType = getRecordType(record);

            const isCourse = recordType === OBJECT_TYPE_COURSE && ((mostRecentStatement.application === LRS_APPID_AEX || mostRecentStatement.application === LRS_APPID_CSO) || mostRecentStatement.context?.subcategory?.endsWith(LEARNING_MODE_COURSEWARE));
            const isCurated = recordType === OBJECT_TYPE_COURSE && mostRecentStatement.application === LRS_APPID_LRNO && (mostRecentStatement.context?.subcategory?.endsWith(LEARNING_MODE_MICROLEARNING) || (mostRecentStatement.context?.subcategory === undefined || mostRecentStatement.context?.subcategory === ''));
            const isModule = recordType === OBJECT_TYPE_MODULE && ((mostRecentStatement.application === LRS_APPID_AEX || mostRecentStatement.application === LRS_APPID_CSO) || mostRecentStatement.context?.subcategory?.endsWith(LEARNING_MODE_COURSEWARE));
            const isTutorial = recordType === OBJECT_TYPE_MODULE && mostRecentStatement.application === LRS_APPID_LRNO && (mostRecentStatement.context?.subcategory?.endsWith(LEARNING_MODE_MICROLEARNING) || (mostRecentStatement.context?.subcategory === undefined || mostRecentStatement.context?.subcategory === ''));
            const isUnit = recordType === OBJECT_TYPE_UNIT;
            const isCollection = recordType === OBJECT_TYPE_COLLECTION;

            const recordsObject = {
              ...record, lastInteraction: mostRecentStatement.timestamp,
            };
            switch (true) {
            case isCourse:
              courseRecords[recordId] = recordsObject;
              break;
            case isCurated:
              curatedListRecords[recordId] = recordsObject;
              break;
            case isModule:
              moduleRecords[recordId] = recordsObject;
              break;
            case isTutorial:
              tutorialRecords[recordId] = recordsObject;
              break;
            case isUnit:
              unitRecords[recordId] = recordsObject;
              break;
            case isCollection:
              collectionRecords[recordId] = recordsObject;
              break;
            default:
              break;
            }
          });

          const tutorialsAndCuratedLists = {
            records: curatedListRecords,
          };

          const coursesAndModules = {
            records: courseRecords,
          };

          const collections = {
            records: collectionRecords,
          };

          Object.keys(moduleRecords).forEach((moduleRecordId) => {
            const moduleRecord = moduleRecords[moduleRecordId];
            if (!allStatementsHaveParent(moduleRecord)) { // Module was consumed in stand-alone state so add it to the list
              coursesAndModules.records[moduleRecordId] = moduleRecord;
            }

            moduleRecord.statements.forEach((statement) => {
              if (statement?.context?.parent
                && coursesAndModules?.records[statement.context.parent]
                && new Date(coursesAndModules.records[statement.context.parent].lastInteraction) < new Date(statement.timestamp)) {
                coursesAndModules.records[statement.context.parent].lastInteraction = statement.timestamp;
              }
            });
          });

          Object.keys(tutorialRecords).forEach((tutorialRecordId) => {
            const tutorialRecord = tutorialRecords[tutorialRecordId];
            if (!allStatementsHaveParent(tutorialRecord)) { // Tutorial was consumed in stand-alone state so add it to the list
              tutorialsAndCuratedLists.records[tutorialRecordId] = tutorialRecord;
            }

            tutorialRecord.statements.forEach((statement) => {
              if (statement?.context?.parent
                && tutorialsAndCuratedLists?.records[statement.context.parent]
                && new Date(tutorialsAndCuratedLists.records[statement.context.parent].lastInteraction) < new Date(statement.timestamp)) {
                tutorialsAndCuratedLists.records[statement.context.parent].lastInteraction = statement.timestamp;
              }
            });
          });

          // Update last interaction date for course / module objects based on child records
          Object.keys(unitRecords).forEach((unitRecordId) => {
            const unitRecord = unitRecords[unitRecordId];
            unitRecord.statements.forEach((statement) => {
              // Check for parent in tutorials and curated lists
              if (statement?.context?.parent
                && tutorialsAndCuratedLists?.records[statement.context.parent]
                && new Date(tutorialsAndCuratedLists.records[statement.context.parent].lastInteraction) < new Date(statement.timestamp)) {
                tutorialsAndCuratedLists.records[statement.context.parent].lastInteraction = statement.timestamp;
              }
              // Check for parent in courses and modules lists
              if (statement?.context?.parent
                && coursesAndModules?.records[statement.context.parent]
                && new Date(coursesAndModules.records[statement.context.parent].lastInteraction) < new Date(statement.timestamp)) {
                coursesAndModules.records[statement.context.parent].lastInteraction = statement.timestamp;
              }
              // Check for grouping in tutorials and curated lists
              if (statement?.context?.grouping
                && tutorialsAndCuratedLists?.records[statement.context.grouping]
                && new Date(tutorialsAndCuratedLists.records[statement.context.grouping].lastInteraction) < new Date(statement.timestamp)) {
                tutorialsAndCuratedLists.records[statement.context.grouping].lastInteraction = statement.timestamp;
              }
              // Check for grouping in courses and modules lists
              if (statement?.context?.grouping
                && coursesAndModules?.records[statement.context.grouping]
                && new Date(coursesAndModules.records[statement.context.grouping].lastInteraction) < new Date(statement.timestamp)) {
                coursesAndModules.records[statement.context.grouping].lastInteraction = statement.timestamp;
              }
            });
          });

          if (action?.payload?.generateDashboardGrouping) {
            tutorialsAndCuratedLists.recent = orderRecentRecordIds(tutorialsAndCuratedLists.records);
            coursesAndModules.recent = orderRecentRecordIds(coursesAndModules.records);
            collections.recent = orderRecentRecordIds(collections.records);

            tutorialsAndCuratedLists.recent.slice(0, 5).forEach((recordId) => {
              const record = tutorialsAndCuratedLists.records[recordId];
              record.data = getDataFetchPromise({
                recordId, record, env, isPreview,
              });
            });

            coursesAndModules.recent.slice(0, 5).forEach((recordId) => {
              const record = coursesAndModules.records[recordId];
              record.data = getDataFetchPromise({
                recordId, record, env, isPreview,
              });
            });

            collections.recent.slice(0, 5).forEach((recordId) => {
              const record = collections.records[recordId];
              record.data = getDataFetchPromise({
                recordId, record, env, isPreview,
              });
            });

            draftState.tutorialsAndCuratedLists = tutorialsAndCuratedLists;
            draftState.coursesAndModules = coursesAndModules;
            draftState.collections = collections;
          }

          if (action?.payload?.generateIndexGrouping) {
            const allRecents = orderRecentRecordIds({ ...tutorialsAndCuratedLists.records, ...coursesAndModules.records, ...collections.records });
            // console.log('updating indexContent allRecents', allRecents?.length);
            const recentSlice = allRecents.slice(0, 5);
            const indexContent = [];
            recentSlice.forEach((recordId) => {
              let dataSource;
              if (tutorialsAndCuratedLists?.records && tutorialsAndCuratedLists.records[recordId]) {
                dataSource = tutorialsAndCuratedLists.records[recordId];
              }
              if (coursesAndModules?.records && coursesAndModules.records[recordId]) {
                dataSource = coursesAndModules.records[recordId];
              }
              if (collections?.records && collections.records[recordId]) {
                dataSource = collections.records[recordId];
              }

              dataSource.data = getDataFetchPromise({
                recordId, record: dataSource, env, isPreview,
              });
              indexContent.push({ id: recordId, ...dataSource });
            });
            // console.log('updating indexContent');
            draftState.indexContent = indexContent;
          }

          logger.info(`${new Date().getTime() - startTime.getTime()} ms taken to get advanced state`);
        }
      });
      return newState;
    },
    [fetchLearningRecords.rejected]: (state, ...params) => {
      // User is not logged in. Ensure removal of user-data from store.
      logger.error('Fetching Learning Records was rejected: ', params);
      return {
        ...state,
        fetchingLearningRecords: false,
        hasFetchedLearningRecords: true,
      };
    },
    [fetchListsData.pending]: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.fetchingListsData = true;
        draftState.hasFetchedListsData = false;
      });
      return newState;
    },
    [fetchListsData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.listsData = action?.payload;
        draftState.hasFetchedListsData = true;
        draftState.fetchingListsData = false;
      });
      return newState;
    },
    [fetchListsData.rejected]: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.listsData = draftState.listsData || [];
        draftState.fetchingListsData = false;
      });
      return newState;
    },
    [updateListData.pending]: (state) => {
      // TODO: Handle pending state
      const newState = produce(state, (draftState) => {
        logger.info(draftState);
      });
      return newState;
    },
    [updateListData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        // Replace list in state with returned updated list
        draftState.listsData = [...draftState.listsData.filter((list) => list.listId !== action?.payload?.listId), action?.payload];
      });
      return newState;
    },
    [updateListData.rejected]: (state) => state,
    [createListData.pending]: (state) => {
      // TODO: Handle pending state
      const newState = produce(state, (draftState) => {
        logger.info(draftState);
      });
      return newState;
    },
    [createListData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        // Push created list into state
        draftState.listsData.push(action?.payload);
      });
      return newState;
    },
    [createListData.rejected]: (state) => state,
    [deleteListData.pending]: (state) => {
      // TODO: Handle pending state
      const newState = produce(state, (draftState) => {
        logger.info(draftState);
      });
      return newState;
    },
    [deleteListData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        // Removing deleted list from store - payload is just listId
        draftState.listsData = draftState.listsData.filter((list) => list.listId !== action?.payload);
      });
      return newState;
    },
    [deleteListData.rejected]: (state) => state,
    [deleteListItem.pending]: (state) => {
      // TODO: Handle pending state
      const newState = produce(state, (draftState) => {
        logger.info(draftState);
      });
      return newState;
    },
    [deleteListItem.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        // Filter out missing entry - Followed list doesn't return fully expanded
        draftState.listsData = draftState.listsData.map((list) => {
          if (list.listId !== action?.payload?.listId) {
            return list;
          }
          if (action?.payload?.listItems?.length) {
            return { ...list, listItems: list.listItems.filter((listItem) => action.payload.listItems.find((payloadItem) => payloadItem.resourceId === listItem.resourceId)) };
          }
          return { ...list, listItems: [] };
        });

        // Removing old copy of list from store and push updated copy from service
        // draftState.listsData = draftState.listsData.filter((list) => list.listId !== action?.payload?.listId).concat(action?.payload);
      });
      return newState;
    },
    [deleteListItem.rejected]: (state) => {
      logger.error('deleteListItem.rejected');
      return state;
    },
    [addListItem.pending]: (state) => {
      // TODO: Handle pending state
      const newState = produce(state, (draftState) => {
        logger.info(draftState);
      });
      return newState;
    },
    [addListItem.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        // Removing old copy of list from store and push updated copy from service
        if (draftState.listsData) {
          draftState.listsData = draftState.listsData.filter((list) => list.listId !== action?.payload?.listId);
          draftState.listsData.push(action?.payload);
        } else {
          draftState.listsData = [action?.payload];
        }
      });
      return newState;
    },
    [addListItem.rejected]: (state) => state,
  },
});

export const {
  setExperimentationContext,
  setUserDetails,
  setLearningRecords,
  clearLearningRecords,
  setSelectedSort,
  toggleDashboardDrawerState,
  setPreferredVolume,
  setIsEditingList,
  setRecentRecordDataPromises,
  enabledSeeAllView,
  setSelectedBookmarkSort,
  setSelectedListsSort,
  dismissLogin,
  setCompletionRecordDataPromises,
} = userSlice.actions;

export const fetchingLearningRecords = (state) => state.userData.fetchingLearningRecords;

export const hasFetchedLearningRecords = (state) => state.userData.hasFetchedLearningRecords;
export const getUserDetails = (state) => state.userData.userDetails;
export const getUserId = (state) => state.userData.userDetails.userid;
export const getOA2Token = (state) => state.userData.userDetails.oa2Token;
export const getLearningRecords = (state) => state.userData?.learningRecords;
// export const getLearningRecords = () => mockRecords;
// export const getCuratedData = (state) => state.userData?.curatedData;
// export const getLearningRecordById = (state, id) => state.userData?.learningRecords[id];
export const isLoaderVisible = (state) => state.userData.isLoaderVisible;
// export const isRecordsCleared = (state) => state.userData.cleared;
// export const lastRecordSetDate = (state) => state.userData.lastRecordSetDate;
export const isAuthChecked = (state) => state.userData.authChecked;
// export const getSelectedSort = (state) => state.userData.selectedSort;
// export const getdashboardState = (state) => state.userData.dashboardState;
export const isUserLoggedIn = (state) => state.userData.authChecked && state.userData.userDetails.userid;
// export const getPreferredVolume = (state) => state.userData.preferredVolume;
// export const getCourseData = (state) => state.userData?.courseData;

export const getLists = (state) => state.userData.listsData;
// export const getListById = (state, id) => state.userData.listsData.find((list) => list.listId === id);
export const isEditingList = (state) => state.userData.isEditingList;
export const isFetchingLists = (state) => state.userData.fetchingListsData;
export const hasFetchedLists = (state) => state.userData.hasFetchedListsData;

export const getExperimentationContext = (state) => state.userData.experimentationContext;
export const userIsInExperimentSegment = (state, expId, segId) => state.userData.experimentationContext && state.userData.experimentationContext[expId] && state.userData.experimentationContext[expId] === segId;

export const getCoursesAndModules = (state) => state.userData.coursesAndModules;
// export const getCoursesAndModules = () => mockTutorialsAndCurateds;
export const getTutorialsAndCuratedLists = (state) => state.userData.tutorialsAndCuratedLists;
// export const getTutorialsAndCuratedLists = () => mockTutorialsAndCurateds;
export const getCollections = (state) => state.userData.collections;
export const getIndexContent = (state) => state.userData.indexContent;
export const getSeeAllView = (state) => state.userData.seeAllView;
export const getSelectedBookmarkSortValue = (state) => state.userData.selectedBookmarkSort;
export const getSelectedListsSortValue = (state) => state.userData.selectedListsSort;
export const isUserProfileFetched = (state) => state.userData?.isUserProfileFetched;
export const isPersonalizedContentLoading = (state) => state.userData.isPersonalizedContentLoading;
export const userTrialEntitlements = (state) => state.userData?.userDetails?.entitlements && state.userData?.userDetails?.entitlements.filter((item) => item.type === PERSONALIZATION_EXPERIMENT_TRIAL_ENTITLEMENT);
export const userCommercialEntitlements = (state) => state.userData?.userDetails?.entitlements && state.userData?.userDetails?.entitlements.filter((item) => item.type === PERSONALIZATION_EXPERIMENT_COMMERCIAL_ENTITLEMENT);

export const getIndustryFilter = (state) => {
  const filterGroups = getChildProperty(state, 'userData.personalizedData.filterGroups');
  let industryFilter = {};
  if (filterGroups?.length) {
    // eslint-disable-next-line consistent-return
    filterGroups.forEach((fg) => {
      const filter = fg?.filters?.find((f) => f.id === 'industry');
      if (filter) {
        industryFilter = { ...filter, selections: filter?.selections?.filter((s) => s.count > 0) || [] };
      }
    });
  }
  return industryFilter;
};
export const getCapabilitiesFilter = (state) => {
  const filterGroups = getChildProperty(state, 'userData.personalizedData.filterGroups');
  let capabilityFilter = {};
  if (filterGroups?.length) {
    // eslint-disable-next-line consistent-return
    filterGroups.forEach((fg) => {
      const filter = fg?.filters?.find((f) => f.id === 'capabilities');
      if (filter) {
        capabilityFilter = { ...filter, selections: filter?.selections?.filter((s) => s.count > 0) || [] };
      }
    });
  }
  return capabilityFilter;
};

export const getShowLoginPrompt = (state) => state.userData.showLoginPrompt;

export default userSlice.reducer;
